/**
 * Reading a collection or a single record out of whatever wrapper the API put
 * around it.
 *
 * `packages/shared` pins the request bodies and the fleet row, but it does not
 * pin whether a list route answers with a bare array or with the array under a
 * name. Rather than guess once and break the panel the day the guess is wrong,
 * the reader accepts the small set of shapes that are actually plausible — the
 * bare value, the value under its own name, or under `items`/`data` — and fails
 * loudly on anything else. The unwrapping happens before validation, so the zod
 * schema still decides whether the contents are usable.
 */

import { z } from 'zod';

const GENERIC_COLLECTION_KEYS = ['items', 'data', 'results'] as const;
const GENERIC_ENTITY_KEYS = ['item', 'data'] as const;

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export function unwrapCollection(payload: unknown, ...names: readonly string[]): unknown {
  if (Array.isArray(payload)) return payload;

  const record = asRecord(payload);
  if (record === null) return payload;

  for (const key of [...names, ...GENERIC_COLLECTION_KEYS]) {
    const candidate = record[key];
    if (Array.isArray(candidate)) return candidate;
  }

  return payload;
}

export function unwrapEntity(payload: unknown, ...names: readonly string[]): unknown {
  const record = asRecord(payload);
  if (record === null) return payload;

  for (const key of [...names, ...GENERIC_ENTITY_KEYS]) {
    const candidate = asRecord(record[key]);
    if (candidate !== null) return candidate;
  }

  return payload;
}

/** A schema for "an array of these, however the route chose to wrap it". */
export function collectionOf<Schema extends z.ZodTypeAny>(
  item: Schema,
  ...names: readonly string[]
): z.ZodType<z.output<Schema>[]> {
  return z.preprocess(
    (payload) => unwrapCollection(payload, ...names),
    z.array(item),
  ) as unknown as z.ZodType<z.output<Schema>[]>;
}

/** A schema for "one of these, however the route chose to wrap it". */
export function entityOf<Schema extends z.ZodTypeAny>(
  item: Schema,
  ...names: readonly string[]
): z.ZodType<z.output<Schema>> {
  return z.preprocess(
    (payload) => unwrapEntity(payload, ...names),
    item,
  ) as unknown as z.ZodType<z.output<Schema>>;
}

/**
 * Accepts a record whose identifier may arrive under either name and normalises
 * it onto `canonical`. Store and screen rows are keyed by `storeId`/`screenId`
 * in the table; a route that serialises them as plain resources may well call
 * the field `id` instead, and both readings are correct.
 */
export function withIdAlias<Schema extends z.ZodTypeAny>(
  item: Schema,
  canonical: string,
  alias = 'id',
): z.ZodType<z.output<Schema>> {
  return z.preprocess((payload) => {
    const record = asRecord(payload);
    if (record === null) return payload;
    if (typeof record[canonical] === 'string') return record;
    if (typeof record[alias] !== 'string') return record;
    return { ...record, [canonical]: record[alias] };
  }, item) as unknown as z.ZodType<z.output<Schema>>;
}
